import React from 'react';
import HighchartsReact from 'highcharts-react-official';
import Skeleton from '@material-ui/lab/Skeleton';

import { Overlay, GeoChart } from 'src/common-ui';
import { KeyFinancialsAccordion } from 'src/common-ui/components/Macros/KeyFinancial/KeyFinancialsAccordion';
import { ChoiceProductivityAccordion } from 'src/common-ui/components/Macros/ChoiceProductivity/ChoiceProductivityAccordion';

import { StateProjection } from './Summary.selectors';
import { SubheaderOwnProps } from 'src/components/Subheader/Subheader.types';
import { summaryContainer } from './Summary.styles';
import Subheader from 'src/components/Subheader/Subheader.container';
import { MACRO_TRENDS_WARNING_MESSAGE } from 'src/pages/Hindsighting/MacroTrends/MacroTrends';
import { StyledAccordion } from 'src/components/StyledAccordion/StyledAccordion';
import Highcharts from 'highcharts';
import { Divider, Grid } from '@material-ui/core';

export interface DispatchProps {
  onShowView(): void;
  onRefetchData(): void;
}

interface MacroTrendAnalysisProps
  extends Pick<SummaryProps, 'dataLoaded' | 'trendAnalysis' | 'productMixAndTrend' | 'globalRegionVolAndTrend'> {}

const MacroTrendAnalysis = ({
  dataLoaded,
  trendAnalysis,
  productMixAndTrend,
  globalRegionVolAndTrend,
}: MacroTrendAnalysisProps) => {
  if (!dataLoaded) {
    return (
      <div className="macro-trend-analysis-items-container">
        <Grid item={true} md={4} data-qa-attribute="graph-container">
          <Skeleton className="chart-container" variant="rect" animation="wave" />
        </Grid>
        <Grid item={true} md={4} data-qa-attribute="graph-container">
          <Skeleton className="chart-container" variant="rect" animation="wave" />
        </Grid>
        <Grid item={true} md={4} data-qa-attribute="graph-container">
          <Skeleton className="chart-container" variant="rect" animation="wave" />
        </Grid>
      </div>
    );
  }

  return (
    <div className="macro-trend-analysis-items-container">
      <Grid item={true} md={4} data-qa-attribute="graph-container">
        <HighchartsReact
          highcharts={Highcharts}
          options={trendAnalysis}
          containerProps={{ className: 'chart-container' }}
        />
      </Grid>
      <Divider orientation="vertical" flexItem style={{ width: 3 }} />
      <Grid item={true} md={4} data-qa-attribute="graph-container">
        <HighchartsReact
          highcharts={Highcharts}
          options={productMixAndTrend}
          containerProps={{ className: 'chart-container' }}
        />
      </Grid>
      <Divider orientation="vertical" flexItem style={{ width: 3 }} />
      <Grid item={true} md={4} data-qa-attribute="graph-container">
        <GeoChart {...globalRegionVolAndTrend} />
      </Grid>
    </div>
  );
};

interface SummaryProps extends StateProjection, DispatchProps {}

export default class Summary extends React.Component<SummaryProps> {
  componentDidMount() {
    this.props.onShowView();
  }

  componentDidUpdate(prevProps: SummaryProps) {
    if (!this.props.dataLoaded || !prevProps.dataLoaded) {
      return;
    }
    const { flowStatus, onRefetchData } = this.props;
    if (prevProps.flowStatus.join('') !== flowStatus.join('')) {
      onRefetchData();
    }
  }

  render() {
    if (!this.props.configLoaded) {
      return <Overlay type="loading" visible={true} />;
    }

    const {
      title,
      dataLoaded,
      viewDataState,
      keyFinancials,
      choiceProductivity,
      trendAnalysis,
      productMixAndTrend,
      globalRegionVolAndTrend,
      showLookBackPeriod,
      showFlowStatus,
    } = this.props;

    const subheaderProps: SubheaderOwnProps = {
      title,
      showFlowStatus,
      showLookBackPeriod,
      errorCondition: MACRO_TRENDS_WARNING_MESSAGE,
      viewDataState,
    };

    return (
      <div className={summaryContainer}>
        <Subheader {...subheaderProps} />
        <div className={'summary-content-container'}>
          <KeyFinancialsAccordion dataLoaded={dataLoaded} macros={keyFinancials} />
          <ChoiceProductivityAccordion dataLoaded={dataLoaded} macros={choiceProductivity} />
          <StyledAccordion expanded={true} title="Macro Trend Analysis" className="flex-accordion">
            <MacroTrendAnalysis
              dataLoaded={dataLoaded}
              trendAnalysis={trendAnalysis}
              productMixAndTrend={productMixAndTrend}
              globalRegionVolAndTrend={globalRegionVolAndTrend}
            />
          </StyledAccordion>
        </div>
      </div>
    );
  }
}
